import { DatePicker, Form, Input, Modal, Checkbox, Button } from "antd";
import React, { useEffect } from "react";
import { svg } from "../../../../Assets/svg";
import dayjs from "dayjs";
import { dayjsFormat } from "../../../../helpers/utils";
import TextArea from "antd/es/input/TextArea";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  milestoneData?: any;
  loading?: boolean;
  showOrderCheckbox?: boolean;
  currencySymbol?: string;
  conversionRate?: number;
}

const PayMilestoneModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  milestoneData,
  loading = false,
  showOrderCheckbox = false,
  currencySymbol = "£",
  conversionRate = 1,
}) => {
  const [form] = Form.useForm();

  // ✅ Pre-fill Form when Modal Opens (Handles New Payment OR Edit Payment)
  useEffect(() => {
    if (open && milestoneData) {
      const isPaid = milestoneData.paid_status === "paid";
      const paidDetails = milestoneData.paid_details || {};

      // 1. Determine Amount
      let initAmount = "";
      if (isPaid && paidDetails.amount_paid) {
        initAmount = (Number(paidDetails.amount_paid) * conversionRate).toFixed(2);
      } else if (milestoneData.remaining_amount !== undefined) {
        initAmount = (Number(milestoneData.remaining_amount) * conversionRate).toFixed(2);
      } else if (milestoneData.calculated_amount) {
        // Fallback for new payment: use calculated amount if available, else value
        const val = milestoneData.calculated_amount;
        initAmount = (Number(val) * conversionRate).toFixed(2);
      }

      // 2. Determine Date
      const initDate =
        isPaid && paidDetails.payment_date
          ? dayjs(paidDetails.payment_date)
          : dayjs();

      // 3. Determine Notes
      const initNote = isPaid ? paidDetails.payment_notes : "";

      // 4. Determine Checkbox
      // If editing, use the saved value. If new, use the prop default.
      const initCreateOrder = isPaid
        ? paidDetails.auto_create_order ?? showOrderCheckbox
        : showOrderCheckbox;

      form.setFieldsValue({
        ...(initAmount ? { paymentAmount: initAmount } : {}),
        paymentDate: initDate,
        note: initNote,
        createOrder: initCreateOrder,
      });
    } else {
      form.resetFields();
    }
  }, [open, milestoneData, form, showOrderCheckbox]);

  const handleFinish = (values: any) => {
    const payload = {
      ...values,
      paymentAmount: values.paymentAmount,
      paymentDate: values.paymentDate ? values.paymentDate.toISOString() : null,
      milestone_id: milestoneData?._id,
      createOrder: showOrderCheckbox ? values.createOrder : false,
    };
    onSave(payload);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      title={
        <div className="text-center fs-4 fw-bolder lh-m mb-4">
          {/* Dynamic Title based on context could be added here, currently sticking to standard */}
          {milestoneData?.paid_status === "paid"
            ? "Edit Payment Details"
            : "Mark As Paid?"}
        </div>
      }
      closeIcon={svg?.closeIcon}
      width={"33.75rem"}
      className="commonModalClass"
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleFinish}
        initialValues={{ createOrder: showOrderCheckbox }}
      >
        <div className="row g-3">
          <div className="col-sm-6">
            <Form.Item
              className="mb-0"
              name="paymentAmount"
              label={<div className="custom-label mb-0 ff">Amount</div>}
              rules={[
                { required: true, message: "Enter Amount" },
                {
                  pattern: /^\d+(\.\d{1,2})?$/,
                  message: "Invalid amount (Max 2 decimals)",
                },
              ]}
              required={false}
            >
              <Input
                className="form-control form-control-lg amountInput"
                placeholder="0"
                prefix={<span className="text-white">{currencySymbol}</span>}
                onChange={(e) =>
                  form.setFieldValue(
                    "paymentAmount",
                    e.target.value.replace(/[^0-9.]/g, ""),
                  )
                }
              />
            </Form.Item>
          </div>

          <div className="col-sm-6">
            <Form.Item
              className="mb-0"
              name="paymentDate"
              label={<div className="custom-label mb-0 ff">Payment Date</div>}
              rules={[{ required: true, message: "Select Date" }]}
              required={false}
            >
              <DatePicker
                style={{ width: "100%" }}
                // removed disabledDate to allow fixing past dates during edit
                className="form-control form-control-lg"
                suffixIcon={svg?.dateIcon}
                placeholder="Select Date"
                format={dayjsFormat}
              />
            </Form.Item>
          </div>

          <div className="col-12">
            <Form.Item
              className="mb-0"
              label={<div className="custom-label mb-0 ff">Payment Notes</div>}
              name="note"
              rules={[
                { required: false, message: "Note is required" },
                { whitespace: true, message: "Note cannot be empty" },
              ]}
            >
              <TextArea
                placeholder="Enter Payment Notes"
                className="form-control bg-white"
                style={{ resize: "none" }}
                rows={4}
              />
            </Form.Item>
          </div>

          {/* {showOrderCheckbox && (
            <div className="col-12">
              <Form.Item
                name="createOrder"
                valuePropName="checked"
                className="mb-2"
              >
                <Checkbox className="fs-6 text-dark">
                  Order will be automatically created from this quote details
                </Checkbox>
              </Form.Item>
            </div>
          )} */}

          <div className="col-12 mt-4">
            <div className="row justify-content-center gx-3">
              <div className="col-6">
                <Button
                  className="btn btn-lg btn-outline-secondary w-100 h-auto"
                  onClick={onClose}
                >
                  CLOSE
                </Button>
              </div>
              <div className="col-6">
                <Button
                  htmlType="submit"
                  className="btn btn-lg btn-primary w-100 h-auto"
                  loading={loading}
                >
                  SAVE
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default PayMilestoneModal;
